import { hasClass } from '@efdigital/ll-addon-ui/utils/css-class-helpers';
import type { CohortAnnouncement } from '@efdigital/ll-xceed-v2/data/builders/xceed/messages';

export const HIDE_ANNOUNCEMENTS_CLASS = 'hide-announcements';

const SEEN_STORAGE_PREFIX = 'll-xceed:cohort-announcements-seen';

function seenStorageKey(programId: string, cohortId: string): string {
  return `${SEEN_STORAGE_PREFIX}:${programId}:${cohortId}`;
}

function readSeenIds(programId: string, cohortId: string): Set<string> {
  try {
    const raw = localStorage.getItem(seenStorageKey(programId, cohortId));
    if (!raw) {
      return new Set();
    }

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return new Set();
    }

    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

function writeSeenIds(
  programId: string,
  cohortId: string,
  seenIds: Set<string>
): void {
  localStorage.setItem(
    seenStorageKey(programId, cohortId),
    JSON.stringify([...seenIds])
  );
}

export function unreadAnnouncementCount(
  programId: string,
  cohortId: string,
  announcements: CohortAnnouncement[]
): number {
  return unreadAnnouncements(programId, cohortId, announcements).length;
}

export function unreadAnnouncements(
  programId: string,
  cohortId: string,
  announcements: CohortAnnouncement[]
): CohortAnnouncement[] {
  const seenIds = readSeenIds(programId, cohortId);
  return announcements.filter((announcement) => !seenIds.has(announcement.id));
}

export function markAnnouncementsSeen(
  programId: string,
  cohortId: string,
  announcementIds: string[]
): void {
  const seenIds = readSeenIds(programId, cohortId);
  for (const id of announcementIds) {
    seenIds.add(id);
  }
  writeSeenIds(programId, cohortId, seenIds);
}

const PREVIEW_MAX_LENGTH = 140;

/** Cohort CSS can hide the announcements section from participants. */
export function cohortShowsAnnouncements(
  css: string | null | undefined
): boolean {
  return !hasClass(css ?? '', HIDE_ANNOUNCEMENTS_CLASS);
}

/** Strip common markdown markup so the dashboard banner can show a one-line preview. */
export function markdownPlainPreview(
  markdown: string,
  maxLength: number = PREVIEW_MAX_LENGTH
): string {
  const text = markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)]\([^)]*\)/g, '$1')
    .replace(/[#*_>~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength).trimEnd()}…`;
}
